import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { verifyEmail } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { createZohoSubscription } from '../services/paymentService';
import { fetchPlans } from '../services/companyService';
import { getLoggedInUserData } from '../helpers/storageUtils';
import { STORAGE_KEYS, PLATFORM_NAME, DISABLE_SUBSCRIPTION } from '../helpers/constants';
import { CheckCircle, ArrowForward } from '@mui/icons-material';
import { decrypt } from '../helpers/encryptionUtils';

const EmailVerifiedSuccess: React.FC = () => {
  const { loginWithResponse } = useAuth();
  const navigate = useNavigate();
  const { token: encryptedToken } = useParams<{ token: string }>();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent multiple API calls
    if (hasVerified.current || !encryptedToken) {
      if (!encryptedToken) {
        setVerificationStatus('error');
        setErrorMessage('Verification token is missing. Please check your email link.');
        setIsVerifying(false);
      }
      return;
    }

    const verifyEmailToken = async () => {
      // Mark as verified to prevent duplicate calls
      hasVerified.current = true;

      try {
        // URL decode the token (since it's URL encoded in the path)
        const urlDecodedToken = decodeURIComponent(encryptedToken);
        
        // Decrypt the token using AES
        const decryptedToken = await decrypt(urlDecodedToken);
        
        // Use the decrypted token for the API call
        const response = await verifyEmail({ token: decryptedToken });
        
        if (response.success && response.access_token) {
          // Update auth context with the response
          loginWithResponse({
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            token_type: response.token_type,
            expires_in: response.expires_in,
            user_id: response.user_id,
            email_id: response.email_id,
            name: response.name,
            role: response.role,
            company_id: response.company_id,
          });
          
          setVerificationStatus('success');
        } else {
          setVerificationStatus('error');
          setErrorMessage(response.message || 'Email verification failed');
        }
      } catch (error: any) {
        console.error('Email verification error:', error);
        setVerificationStatus('error');
        setErrorMessage(error.message || 'Failed to verify email. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmailToken();
  }, [encryptedToken, loginWithResponse]);

  const handleGetStarted = async () => {
    // When subscription is disabled, skip subscription creation and go to onboarding (Welcome step only, then supportSpace)
    if (DISABLE_SUBSCRIPTION) {
      navigate('/onboarding');
      return;
    }
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      setSubscriptionError('Session expired. Please log in again.');
      return;
    }
    const userData = getLoggedInUserData();
    if (!userData?.company_id || !userData?.email_id) {
      setSubscriptionError('Missing account details. Please log in again.');
      return;
    }
    setIsCreatingSubscription(true);
    setSubscriptionError('');
    try {
      // Fetch plans and pick the $0 plan (name + planCode) for createZohoSubscription
      const plansResponse = await fetchPlans();
      const plans = plansResponse?.data ?? [];
      const zeroPricePlan = plans.find((p) => {
        const price = parseFloat(String(p.monthlyPrice || '').replace(/[^0-9.]/g, ''));
        return price === 0;
      });
      if (!zeroPricePlan) {
        setSubscriptionError('No free plan found. Please contact support.');
        return;
      }
      // Customer object same as planUpgradeService: displayName = companyName, email, companyName
      const companyName =
        (userData as { company_name?: string; companyName?: string }).company_name ||
        (userData as { company_name?: string; companyName?: string }).companyName ||
        userData.name ||
        '';
      await createZohoSubscription(
        {
          plan: {
            name: zeroPricePlan.name,
            planCode: zeroPricePlan.planCode,
          },
          customer: {
            displayName: companyName,
            email: userData.email_id,
            companyName: companyName,
          },
          company_id: userData.company_id,
          platform_name: PLATFORM_NAME,
        },
        refreshToken
      );
      navigate('/onboarding');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create subscription. Please try again.';
      setSubscriptionError(message);
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        {isVerifying ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Verifying your email...
            </Typography>
          </Box>
        ) : verificationStatus === 'success' ? (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CheckCircle 
                sx={{ 
                  fontSize: 64, 
                  color: 'success.main', 
                  mb: 2 
                }} 
              />
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Email Verified Successfully!
              </Typography>
            </Box>
            
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              icon={<CheckCircle />}
            >
              <Typography variant="body1">
                Your email has been verified successfully. You can now proceed to set up your account.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Click the button below to get started with your onboarding process.
            </Typography>

            {subscriptionError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubscriptionError('')}>
                {subscriptionError}
              </Alert>
            )}

            <Button
              onClick={handleGetStarted}
              fullWidth
              variant="contained"
              size="large"
              disabled={isCreatingSubscription}
              endIcon={isCreatingSubscription ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
              sx={{ 
                mt: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              {isCreatingSubscription ? 'Setting up...' : 'Get Started'}
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Verification Failed
              </Typography>
            </Box>
            
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
            >
              <Typography variant="body1">
                {errorMessage}
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              The verification link may have expired or is invalid. Please request a new verification email or contact support.
            </Typography>

            <Button
              onClick={() => navigate('/login')}
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Go to Login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EmailVerifiedSuccess;

